import React, { useEffect, useState } from "react";
import api from "./api";
import { Card, Button, Chip, BackButton, Input } from "./ui";

export const ALLY_CATS = [
  { key: "veterinaria", label: "Veterinaria", emoji: "🏥" },
  { key: "grooming", label: "Grooming", emoji: "✂️" },
  { key: "entrenador", label: "Entrenador", emoji: "🎓" },
  { key: "tienda", label: "Tienda pet", emoji: "🛍️" },
  { key: "guarderia", label: "Guardería / hotel", emoji: "🏡" },
  { key: "paseador", label: "Paseador", emoji: "🦮" },
  { key: "fotografo", label: "Fotógrafo pet", emoji: "📸" },
  { key: "cafe", label: "Café pet-friendly", emoji: "☕" },
  { key: "eventos", label: "Eventos", emoji: "🎉" },
];
const catInfo = (k) => ALLY_CATS.find((c) => c.key === k) || { label: k, emoji: "🐾" };
const WA_MSG = "Hola, vengo de PawCita y quiero consultar sobre sus servicios para mi perro.";

function openWhatsApp(ally) {
  api.allies.event(ally.id, "whatsapp_click").catch(() => {});
  window.open(`https://wa.me/${ally.whatsapp}?text=${encodeURIComponent(WA_MSG)}`, "_blank", "noopener");
}

function Badges({ ally }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ally.is_founder && <Chip tone="yellow" className="text-[11px]">⭐ Aliado Fundador</Chip>}
      {ally.is_verified && <Chip tone="mint" className="text-[11px]">✓ Aliado Verificado</Chip>}
    </div>
  );
}

export default function Allies() {
  const [allies, setAllies] = useState([]);
  const [cat, setCat] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = (c = cat, term = search) => {
    setLoading(true);
    setError("");

    api.allies
      .list(c, term.trim())
      .then((list) => {
        setAllies(list);
      })
      .catch((e) => setError(e.message || "No se pudieron cargar los aliados."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    setError("");

    api.allies
      .list("", "")
      .then((list) => {
        setAllies(list);
      })
      .catch((e) => setError(e.message || "No se pudieron cargar los aliados."))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = (a) => {
    api.allies.event(a.id, "view").catch(() => {});
    setError("");
    api.allies.detail(a.id).then(setSelected).catch((e) => {
      setError(e.message || "No se pudo cargar el detalle del aliado.");
    });
  };

  if (selected) {
    const info = catInfo(selected.category);
    return (
      <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="ally-detail-screen">
        <BackButton onClick={() => setSelected(null)} testId="ally-detail-back-btn" />
        <Card className="mt-4 overflow-hidden p-0">
          <div className="h-44 w-full bg-slate-100">
            {selected.image ? (
              <img src={selected.image} alt={selected.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-5xl">{info.emoji}</div>
            )}
          </div>
          <div className="p-5">
            <Badges ally={selected} />
            <h1 className="mt-2 break-words font-display text-2xl font-black leading-snug text-[#0B2D4D] sm:text-3xl">{selected.name}</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">{info.emoji} {info.label} · {selected.city}{selected.zone ? ` · ${selected.zone}` : ""}</p>
            {selected.long_description && <p className="mt-3 text-sm text-slate-600">{selected.long_description}</p>}
            {selected.services?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                  Especialidades y servicios
                </p>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selected.services.map((s, i) => (
                    <Chip key={i} tone="gray" className="text-[11px]">
                      {s}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
            {selected.benefit && (
              <div className="mt-3 rounded-2xl bg-emerald-50 p-3 text-sm font-bold text-emerald-800" data-testid="ally-benefit">🎁 Beneficio PawCita: {selected.benefit}</div>
            )}
            {selected.address && <p className="mt-3 text-sm text-slate-500">📍 {selected.address}</p>}
            {selected.hours && <p className="mt-1 text-sm text-slate-500">🕒 {selected.hours}</p>}
            <Button variant="primary" onClick={() => openWhatsApp(selected)} testId="ally-detail-whatsapp-btn" className="mt-4 w-full py-4 text-base">
              <span>💬</span><span>Contactar por WhatsApp</span>
            </Button>
            <div className="mt-2 flex gap-2">
              {selected.instagram && (
                <Button variant="outline" className="flex-1" testId="ally-instagram-btn"
                  onClick={() => { api.allies.event(selected.id, "instagram_click").catch(() => {}); window.open(selected.instagram.startsWith("http") ? selected.instagram : `https://instagram.com/${selected.instagram.replace("@", "")}`, "_blank", "noopener"); }}>
                  📸 Instagram
                </Button>
              )}
              {selected.website && (
                <Button variant="outline" className="flex-1" testId="ally-website-btn"
                  onClick={() => { api.allies.event(selected.id, "website_click").catch(() => {}); window.open(selected.website.startsWith("http") ? selected.website : `https://${selected.website}`, "_blank", "noopener"); }}>
                  🌐 Web
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="allies-screen">
      <h1 className="font-display text-3xl font-black text-[#0B2D4D]">Aliados PawCita</h1>

      <p className="mt-1 text-sm text-slate-500">
        Encuentra aliados según lo que necesita tu perro.
      </p>

      <form
        className="mt-4 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          load(cat, search);
        }}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Busca por nombre o especialidad, ej. ortopedia"
          className="flex-1"
          testId="allies-search"
        />

        <Button type="submit" className="sm:self-end">
          Buscar
        </Button>

        {search ? (
          <Button
            type="button"
            variant="outline"
            className="sm:self-end"
            onClick={() => {
              setSearch("");
              load(cat, "");
            }}
          >
            Limpiar
          </Button>
        ) : null}
      </form>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="allies-filters">
        <button onClick={() => { setCat(""); load("", search); }} data-testid="ally-filter-all"
          className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-bold ${cat === "" ? "bg-[#0B2D4D] text-white" : "bg-white text-slate-500 border border-slate-200"}`}>Todos</button>
        {ALLY_CATS.map((c) => (
          <button key={c.key} onClick={() => { setCat(c.key); load(c.key, search); }} data-testid={`ally-filter-${c.key}`}
            className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-bold ${cat === c.key ? "bg-[#0B2D4D] text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{c.emoji} {c.label}</button>
        ))}
      </div>
      {loading ? (
        <p className="mt-8 text-center text-sm text-slate-400">Cargando aliados…</p>
      ) : error ? (
        <Card className="mt-6 p-8 text-center" data-testid="allies-error">
          <p role="alert" className="text-sm font-semibold text-rose-700">{error}</p>
          <Button className="mt-4" onClick={() => load()}>Reintentar</Button>
        </Card>
      ) : allies.length === 0 ? (
        <Card className="mt-6 p-8 text-center" data-testid="allies-empty">
          <p className="text-4xl">🐾</p>
          <p className="mt-2 font-bold text-[#0B2D4D]">
            {search
              ? `No encontramos aliados para “${search}”.`
              : "Pronto encontrarás aliados PawCita cerca de ti 🐾"}
          </p>
        </Card>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="allies-list">
          {allies.map((a) => {
            const info = catInfo(a.category);
            return (
              <Card key={a.id} className="flex flex-col p-0 overflow-hidden" data-testid={`ally-card-${a.id}`}>
                <button onClick={() => openDetail(a)} className="block text-left">
                  <div className="h-32 w-full bg-slate-100">
                    {a.image ? <img src={a.image} alt={a.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-4xl">{info.emoji}</div>}
                  </div>
                  <div className="p-4">
                    <Badges ally={a} />
                    <p className="mt-1 font-display text-lg font-black leading-snug text-[#0B2D4D] break-words">{a.name}</p>
                    <p className="text-xs font-semibold text-slate-500">{info.emoji} {info.label} · {a.city}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.short_description}</p>

                    {a.services?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {a.services.slice(0, 3).map((service, i) => (
                          <Chip key={i} tone="gray" className="text-[10px]">
                            {service}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {a.benefit && <p className="mt-2 text-xs font-bold text-emerald-700">🎁 {a.benefit}</p>}
                  </div>
                </button>
                <div className="mt-auto px-4 pb-4">
                  <Button variant="primary" onClick={() => openWhatsApp(a)} testId={`ally-whatsapp-${a.id}`} className="w-full">
                    <span>💬</span><span>Contactar por WhatsApp</span>
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
